// =========================================================================
// Game Controller (state machine、engine ↔ UI)
// =========================================================================
// spec/closing_river_web_mvp_v2.md
//   - 「Engine Adapter controller 擬似コード」
//   - 「End Overlay」
//   - 「画面フロー」
//   - 「アニメーション」 / 「入力ロック」
//   - 「ハプティック」
//   - 「Progress badge semantics」
//
// state machine:
//
//   idle
//    │ start()
//    ▼
//   selecting ──── EdgeInteraction edgeSelected ─▶ preview (ConfirmModal 表示)
//     ▲                                                │  onCancel
//     └────────────────────────────────────────────────┘
//                                                       │ onConfirm
//                                                       ▼
//                                                  confirming (lock 開始)
//                                                       │ applyOperation
//                                                       ▼
//                                                   animating (lock 中)
//                                                       │ fallback timeout
//                                            ┌──────────┴──────────┐
//                                            │ judge.in_progress    │ judge.win/loss
//                                            ▼                      ▼
//                                       selecting                    end (EndOverlay)
//
// 担当: W5
// 依存:
//   W3: CanvasHandle              (canvas.ts, update / onEvent / destroy)
//   W4: EdgeInteractionHandle     (edge-interaction.ts, attach / onEvent / clearSelection)
//   W4: ConfirmModalHandle        (confirm-modal.ts, show(preview, onConfirm, onCancel))
//   W4: TurnIndicatorHandle       (turn-indicator.ts, update(movesUsed, N))
//   W4: renderPreviewBar(container, preview | null)  (preview.ts, pure 関数)
//   W4: buildEdgePreviewMap(state) → Map<edgeId, EdgePreview>
//   W5: EndOverlayHandle          (end-overlay.ts、本 worker 担当)
// =========================================================================

import type {
  ApplyResult,
  EdgeId,
  GameState,
  JudgeResult,
} from '../engine-adapter.js';
import {
  buildCleanLogFromResults,
  checkResult,
  evaluateWin,
  loadBoard,
  operate,
} from '../engine-adapter.js';
import type { Badge, BoardViewModel, PlayableBoard } from '../catalog/manifest.js';
import { circleLayout } from '../layout/circle.js';

import type { CanvasHandle } from './canvas.js';
import type {
  EdgeInteractionEvent,
  EdgeInteractionHandle,
} from './edge-interaction.js';
import type { ConfirmModalHandle } from './confirm-modal.js';
import type { TurnIndicatorHandle } from './turn-indicator.js';
import type { EdgePreview } from './preview.js';
import { buildEdgePreviewMap, renderPreviewBar } from './preview.js';
import type { EndOverlayHandle, EndOverlayResult } from './end-overlay.js';

// -------------------------------------------------------------------------
// 公開 API
// -------------------------------------------------------------------------

export interface GameControllerOptions {
  readonly canvas: CanvasHandle;
  readonly edgeInteraction: EdgeInteractionHandle;
  readonly confirmModal: ConfirmModalHandle;
  readonly turnIndicator: TurnIndicatorHandle;
  /**
   * preview-bar の描画先 (= 画面下部固定の container)。
   * controller が renderPreviewBar(container, preview | null) を呼ぶ。
   */
  readonly previewBarContainer: HTMLElement;
  readonly endOverlay: EndOverlayHandle;
  readonly board: PlayableBoard;
  /** W7 progress.markComplete 連携 (勝利時のみ、敗北時は呼ばない) */
  readonly onComplete: (badge: Badge) => void;
  /** タイトル / リストへ戻るときの hook */
  readonly onExit: (reason: 'home' | 'pickAnother') => void;
  /** SVG 描画領域の論理サイズ。layout/circle に渡す。既定 360x360 */
  readonly canvasSize?: { readonly width: number; readonly height: number };
  /**
   * solverFingerprint が catalog と環境で不一致のときに null を渡す。
   * 未指定なら board.shortest を使う (= Version Gate 通過想定)。
   */
  readonly catalogShortest?: number | null;
  /**
   * アニメ完了待ちの timeout (canvas が自前で lockBriefly するため、controller は
   * その間 入力受付しないだけ)。 既定: 350ms (consume 150 + 合流 250 のうち長い方
   * + マージン)。
   */
  readonly animationFallbackMs?: number;
}

export interface GameControllerHandle {
  start(): void;
  destroy(): void;
  /** テスト/inspector 用: 現在の controller state machine 状態 */
  getState(): ControllerState;
}

export type ControllerState =
  | 'idle'
  | 'selecting'
  | 'preview'
  | 'confirming'
  | 'animating'
  | 'end';

// -------------------------------------------------------------------------
// 実装
// -------------------------------------------------------------------------

const DEFAULT_CANVAS_SIZE = { width: 360, height: 360 } as const;
const DEFAULT_ANIMATION_FALLBACK_MS = 350;

export function createGameController(
  opts: GameControllerOptions,
): GameControllerHandle {
  // ----- 不変参照 ---------------------------------------------------------
  const canvasSize = opts.canvasSize ?? DEFAULT_CANVAS_SIZE;
  const animationFallbackMs =
    opts.animationFallbackMs ?? DEFAULT_ANIMATION_FALLBACK_MS;
  const catalogShortest =
    opts.catalogShortest === undefined ? opts.board.shortest : opts.catalogShortest;

  // ----- mutable state ----------------------------------------------------
  let state: ControllerState = 'idle';
  let gameState: GameState = loadBoard(opts.board);
  const applyResults: ApplyResult[] = [];
  let selectedEdgeId: EdgeId | null = null;
  /** 現在の盤面の UI preview map (edgeId → UI EdgePreview) */
  let previewMap: Map<number, EdgePreview> = new Map();
  let destroyed = false;
  /** EdgeInteraction onEvent listener (destroy で外す) */
  let interactionListener: ((ev: EdgeInteractionEvent) => void) | null = null;

  // ----- 起動 -------------------------------------------------------------
  function start(): void {
    if (state !== 'idle') {
      throw new Error(`GameController.start: already started (state=${state})`);
    }
    // EdgeInteraction を canvas に attach + listener 登録
    opts.edgeInteraction.attach(opts.canvas);
    interactionListener = (ev) => {
      if (destroyed) return;
      handleInteractionEvent(ev);
    };
    opts.edgeInteraction.onEvent(interactionListener);

    previewMap = buildEdgePreviewMap(gameState);
    transitionTo('selecting');
    renderAll();
  }

  // ----- EdgeInteraction event 処理 -------------------------------------
  function handleInteractionEvent(ev: EdgeInteractionEvent): void {
    // 入力ロック相当: selecting と preview 以外は無視
    if (state !== 'selecting' && state !== 'preview') return;

    if (ev.type === 'edgeSelected') {
      const edgeId = ev.edgeId;
      const preview = previewMap.get(edgeId);
      if (!preview) {
        // タップ可能でない edge (= UI 反映待ち or バグ)。 状態は変えない
        return;
      }
      selectedEdgeId = edgeId;
      renderPreviewBar(opts.previewBarContainer, preview);

      // preview 状態に入って ConfirmModal を表示
      transitionTo('preview');
      renderAll();
      opts.confirmModal.show(
        preview,
        () => onConfirm(preview),
        () => onCancel(),
      );
      return;
    }

    // edgeDeselected (外部からの強制解除)
    if (ev.type === 'edgeDeselected') {
      if (state === 'preview') {
        // ConfirmModal を閉じて selecting に戻す
        opts.confirmModal.hide();
      }
      selectedEdgeId = null;
      renderPreviewBar(opts.previewBarContainer, null);
      transitionTo('selecting');
      renderAll();
      return;
    }
  }

  function onCancel(): void {
    if (destroyed) return;
    if (state !== 'preview') return;
    selectedEdgeId = null;
    renderPreviewBar(opts.previewBarContainer, null);
    opts.edgeInteraction.clearSelection();
    transitionTo('selecting');
    renderAll();
  }

  function onConfirm(preview: EdgePreview): void {
    if (destroyed) return;
    if (state !== 'preview') return;
    confirmSelected(preview);
  }

  // ----- 確定 → operate → animate → judge ------------------------------
  function confirmSelected(preview: EdgePreview): void {
    transitionTo('confirming');
    // ConfirmModal は doConfirm 内で既に閉じられている (W4 ConfirmModal 実装より)

    let result: ApplyResult;
    try {
      result = operate(gameState, preview.edgeId);
    } catch (err) {
      // applyOperation が throw する状況は engine 側の不整合 (= edge 消滅後の
      // late confirm 等)。 selecting に戻して preview を再計算する
      // eslint-disable-next-line no-console
      console.error('GameController: operate threw', err);
      previewMap = buildEdgePreviewMap(gameState);
      selectedEdgeId = null;
      opts.edgeInteraction.clearSelection();
      renderPreviewBar(opts.previewBarContainer, null);
      transitionTo('selecting');
      renderAll();
      return;
    }

    applyResults.push(result);
    gameState = result.nextState;

    // 合流時のハプティック (spec: vibrate(25))
    if (result.operationKind === 'merge') {
      tryVibrate(25);
    }

    // 入力ロック相当: animating に遷移し、preview/select をクリアして再描画
    transitionTo('animating');
    selectedEdgeId = null;
    renderPreviewBar(opts.previewBarContainer, null);
    // EdgeInteraction 側の選択も外す (clearSelection は edgeDeselected を emit する
    // が、interactionListener は state==='animating' で無視する)
    opts.edgeInteraction.clearSelection();
    renderAll();

    // canvas 側で自前で lockBriefly しているので、controller は固定 timeout で
    // 入力受付を再開する。 destroy / late callback の保険を入れる。
    window.setTimeout(() => {
      if (destroyed) return;
      if (state !== 'animating') return;
      afterAnimation();
    }, animationFallbackMs);
  }

  function afterAnimation(): void {
    const j: JudgeResult = checkResult(gameState);
    if (j.kind === 'in_progress') {
      previewMap = buildEdgePreviewMap(gameState);
      transitionTo('selecting');
      renderAll();
      return;
    }
    handleTerminal(j);
  }

  // ----- 終局処理 ---------------------------------------------------------
  function handleTerminal(j: JudgeResult): void {
    transitionTo('end');
    renderPreviewBar(opts.previewBarContainer, null);

    if (j.kind === 'win') {
      const cleanLog = buildCleanLogFromResults(applyResults);
      const beauty = evaluateWin(gameState, cleanLog, catalogShortest);
      const badge: Badge = {
        cleared: beauty.clear,
        clean: beauty.clean,
        silent: beauty.silent,
        bestMoves: beauty.silent ? gameState.movesUsed : undefined,
      };
      opts.onComplete(badge);

      const overlayResult: EndOverlayResult = {
        kind: 'win',
        badge,
        movesUsed: j.movesUsed,
        shortest: opts.board.shortest,
        N: opts.board.N,
      };
      opts.endOverlay.show(overlayResult, buildOverlayCallbacks());
      renderAll();
      return;
    }

    // 敗北
    const reason = mapLossReason(j);
    const overlayResult: EndOverlayResult = {
      kind: 'lose',
      reason,
      movesUsed: extractMovesUsed(j),
      N: opts.board.N,
    };
    opts.endOverlay.show(overlayResult, buildOverlayCallbacks());
    renderAll();
  }

  function buildOverlayCallbacks() {
    return {
      onRetry: () => {
        if (destroyed) return;
        retry();
      },
      onPickAnother: () => {
        if (destroyed) return;
        opts.endOverlay.hide();
        opts.onExit('pickAnother');
      },
      onHome: () => {
        if (destroyed) return;
        opts.endOverlay.hide();
        opts.onExit('home');
      },
    };
  }

  // ----- リトライ ---------------------------------------------------------
  function retry(): void {
    opts.endOverlay.hide();
    gameState = loadBoard(opts.board);
    applyResults.length = 0;
    selectedEdgeId = null;
    opts.edgeInteraction.clearSelection();
    renderPreviewBar(opts.previewBarContainer, null);
    previewMap = buildEdgePreviewMap(gameState);
    state = 'idle';
    transitionTo('selecting');
    renderAll();
  }

  // ----- ViewModel build + render --------------------------------------
  function renderAll(): void {
    const vm = buildViewModel();
    opts.canvas.update(vm);
    opts.turnIndicator.update(gameState.movesUsed, gameState.turnLimit);
  }

  function buildViewModel(): BoardViewModel {
    const nodeIds = Array.from(gameState.nodes.keys());
    const hintPositions = opts.board.layoutHints?.positions;
    const positions = hintPositions
      ? resolveHintedPositions(nodeIds, hintPositions, canvasSize)
      : circleLayout(nodeIds, canvasSize.width, canvasSize.height);

    const nodes = nodeIds.map((id) => {
      const data = gameState.nodes.get(id)!;
      const pos = positions[id] ?? { x: canvasSize.width / 2, y: canvasSize.height / 2 };
      const previewValue = computeNodePreviewValue(id);
      return {
        id,
        value: data.value,
        x: pos.x,
        y: pos.y,
        ...(previewValue !== undefined ? { previewValue } : {}),
      };
    });

    const tappableSet = new Set(previewMap.keys());
    const activeEdges = Array.from(gameState.activeEdges.values()).map((e) => {
      let highlight: 'none' | 'tappable' | 'selected' = 'none';
      if (selectedEdgeId === e.id) {
        highlight = 'selected';
      } else if (
        tappableSet.has(e.id) &&
        (state === 'selecting' || state === 'preview')
      ) {
        highlight = 'tappable';
      }
      return { id: e.id, src: e.src, dst: e.dst, highlight };
    });

    const fallback: readonly [number, number] = [
      canvasSize.width / 2,
      canvasSize.height / 2,
    ];
    const historyEdges = Array.from(gameState.historyEdges.values()).map((h) => {
      const ep = h.endpointPositionsAtDeletion ?? [fallback, fallback];
      const age = Math.max(0, gameState.currentStep - h.deletedAtStep);
      return {
        id: h.id,
        fromPos: ep[0],
        toPos: ep[1],
        age,
        reason: h.deletedReason,
      };
    });

    return {
      boardId: opts.board.id,
      dimension: opts.board.dimension,
      nodes,
      activeEdges,
      historyEdges,
      movesUsed: gameState.movesUsed,
      turnLimit: gameState.turnLimit,
      seedLabel: opts.board.seed,
    };
  }

  function computeNodePreviewValue(nodeId: number): number | undefined {
    if (selectedEdgeId === null) return undefined;
    const p = previewMap.get(selectedEdgeId);
    if (!p) return undefined;
    // engine の activeEdges から src/dst を引いて preview を node に紐付ける
    const edge = gameState.activeEdges.get(selectedEdgeId);
    if (!edge) return undefined;
    if (p.isMerge) {
      if (nodeId === edge.src || nodeId === edge.dst) return p.srcAfter;
      return undefined;
    }
    // subtract: src/dst のうち srcAfter !== srcValue 側が「値が変わるノード」
    if (nodeId === edge.src && p.srcAfter !== p.srcValue) return p.srcAfter;
    if (nodeId === edge.dst && p.dstAfter !== p.dstValue) return p.dstAfter;
    return undefined;
  }

  // ----- transition helper -----------------------------------------------
  function transitionTo(next: ControllerState): void {
    state = next;
  }

  // ----- destroy ----------------------------------------------------------
  function destroy(): void {
    destroyed = true;
    state = 'end';
    if (interactionListener) {
      opts.edgeInteraction.offEvent(interactionListener);
      interactionListener = null;
    }
    opts.edgeInteraction.detach();
    // 子 handle (canvas/preview/confirm/turn/overlay) の所有権は呼び出し側
    // (= main.ts / W7) にあるので、ここで destroy は呼ばない。
    // ただし overlay は controller が show したので hide する。
    opts.endOverlay.hide();
  }

  function getState(): ControllerState {
    return state;
  }

  return { start, destroy, getState };
}

// -------------------------------------------------------------------------
// helpers
// -------------------------------------------------------------------------

function tryVibrate(arg: number | readonly number[]): void {
  if (typeof navigator === 'undefined') return;
  if (!('vibrate' in navigator)) return;
  try {
    if (Array.isArray(arg)) {
      navigator.vibrate(arg.slice());
    } else {
      navigator.vibrate(arg as number);
    }
  } catch {
    // 一部 browser で permission/security policy により throw → silent
  }
}

function mapLossReason(
  j: JudgeResult,
): 'turnLimit' | 'noEdges' | 'disconnected' {
  switch (j.kind) {
    case 'loss_turn_limit':
      return 'turnLimit';
    case 'loss_no_edges':
      return 'noEdges';
    case 'loss_disconnected':
      return 'disconnected';
    default:
      return 'turnLimit';
  }
}

function extractMovesUsed(j: JudgeResult): number {
  if (j.kind === 'in_progress') return 0;
  return j.movesUsed;
}

function resolveHintedPositions(
  nodeIds: readonly number[],
  hints: Record<string, { readonly x: number; readonly y: number }>,
  canvasSize: { readonly width: number; readonly height: number },
): Record<number, { x: number; y: number }> {
  const out: Record<number, { x: number; y: number }> = {};
  const missing: number[] = [];
  for (const id of nodeIds) {
    const h = hints[String(id)];
    if (h) {
      out[id] = { x: h.x, y: h.y };
    } else {
      missing.push(id);
    }
  }
  if (missing.length > 0) {
    const fallback = circleLayout(missing, canvasSize.width, canvasSize.height);
    for (const id of missing) {
      out[id] = fallback[id]!;
    }
  }
  return out;
}
